/**
 * EnvVersionBadge — 비운영(dev/dev-test) 빌드에서만 화면 좌하단에 rc 버전 + QA 피드백 컨트롤 표시.
 *
 * QA 검증팀이 "지금 보는 게 검증 대상 버전이 맞는지"를 매번 확인/문의하지 않도록 버전을 상시 노출하고,
 * 배지를 클릭하면 팝오버로 캡처(⌘⇧K)/영역 캡처(⌘⇧A)/QA 피드백 모드 ON·OFF 를 제공한다.
 * ×(닫기) 는 완전히 숨기지 않고 좌하단의 작은 버튼으로 접어, 다시 눌러 꺼낼 수 있다.
 *
 * 캡처 시 배지·팝오버가 스크린샷에 찍히지 않도록 `data-screenshot-exclude` 로 제외하고,
 * 캡처 액션을 누르면 팝오버를 닫는다(ScreenCaptureService 가 해당 속성 요소를 필터).
 *
 * 운영(nextpod-ai) 빌드에서는 절대 렌더하지 않는다 → 관련 QA 코드가 DCE 로 제거된다.
 * (.agent/workflows/qa-release-process.md, .agent/plans/qa-feedback-mode.md)
 */
import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, Crop, Copy, ToggleLeft, ToggleRight, MessageSquareWarning } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import {
  IS_PROD, isQaModeEnabled, setQaModeEnabled, subscribeQaMode,
  requestQaCapture, requestQaAreaCapture,
} from '../../config/qaFeedback';

// 네이티브 앱에는 물리 키보드가 없어 단축키 힌트(⌘⇧K 등)를 표시하지 않는다
const isNative = Capacitor.isNativePlatform();

const EnvVersionBadge: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(false);
  const [qaMode, setQaMode] = useState(() => isQaModeEnabled());

  useEffect(() => subscribeQaMode(setQaMode), []);

  if (IS_PROD) return null;

  // vite.config.ts define 주입 — git describe(예: v1.4.0.4.1-rc.2) + short SHA
  const describe = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';
  const commit = typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : 'unknown';

  const copyVersion = () => {
    const text = `${describe} (${commit})`;
    navigator.clipboard?.writeText(text).then(
      () => toast({ description: `버전 복사됨: ${text}` }),
      () => {/* 클립보드 불가 환경 무시 */},
    );
  };

  // 캡처: 팝오버를 먼저 닫고 캡처 요청 (스크린샷에 팝오버가 남지 않도록)
  const handleCapture = () => { setOpen(false); requestQaCapture(); };
  const handleAreaCapture = () => { setOpen(false); requestQaAreaCapture(); };

  // 접힌 상태: 좌하단 작은 버튼만 표시 (다시 눌러 펼침)
  if (collapsed) {
    return (
      <button
        type="button"
        data-screenshot-exclude
        onClick={() => setCollapsed(false)}
        className="fixed bottom-2 left-2 z-[60] pointer-events-auto flex items-center justify-center size-6 rounded-full bg-muted/80 text-muted-foreground shadow-sm backdrop-blur-sm hover:text-foreground transition-colors"
        title="빌드 버전 · QA 피드백 표시"
        aria-label="show build version"
      >
        <MessageSquareWarning className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <div
      data-screenshot-exclude
      className="fixed bottom-2 left-2 z-[60] pointer-events-auto flex items-center gap-1 rounded-control bg-muted/80 px-2 py-1 text-meta font-mono text-muted-foreground shadow-sm backdrop-blur-sm select-none"
      role="status"
      aria-label="build version"
    >
      {/* modal: 팝오버 밖 터치는 "닫기만" 하고 아래 요소에 클릭이 전달되지 않도록 (click-through 방지) */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 cursor-pointer hover:text-foreground transition-colors"
            title="QA 피드백 · 버전 정보"
          >
            <span>{describe}</span>
            {qaMode && !isNative && (
              <span className="text-meta text-primary" aria-hidden>⌘⇧K</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent data-screenshot-exclude side="top" align="start" className="w-60 p-2">
          <div className="flex flex-col gap-0.5">
            <div className="px-2 py-1.5 text-caption font-semibold text-foreground">
              QA 피드백
            </div>

            {/* 전체 캡처 */}
            <button
              type="button"
              onClick={handleCapture}
              disabled={!qaMode}
              className="flex items-center justify-between gap-2 rounded-control px-2 py-1.5 text-label text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <span className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-muted-foreground" />
                전체 캡처
              </span>
              {!isNative && (
                <span className="text-meta font-mono text-muted-foreground">⌘⇧K</span>
              )}
            </button>

            {/* 영역 캡처 */}
            <button
              type="button"
              onClick={handleAreaCapture}
              disabled={!qaMode}
              className="flex items-center justify-between gap-2 rounded-control px-2 py-1.5 text-label text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <span className="flex items-center gap-2">
                <Crop className="w-4 h-4 text-muted-foreground" />
                영역 캡처
              </span>
              {!isNative && (
                <span className="text-meta font-mono text-muted-foreground">⌘⇧A</span>
              )}
            </button>

            <div className="my-1 h-px bg-border" />

            {/* QA 모드 토글 */}
            <button
              type="button"
              onClick={() => setQaModeEnabled(!qaMode)}
              className="flex items-center justify-between gap-2 rounded-control px-2 py-1.5 text-label text-foreground hover:bg-muted transition-colors"
            >
              <span>QA 피드백 모드</span>
              {qaMode
                ? <ToggleRight className="w-5 h-5 text-primary" />
                : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
            </button>

            <div className="my-1 h-px bg-border" />

            {/* 버전 복사 */}
            <button
              type="button"
              onClick={copyVersion}
              className="flex items-center gap-2 rounded-control px-2 py-1.5 text-label text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              title="버전+커밋 복사"
            >
              <Copy className="w-4 h-4" />
              <span className="font-mono text-meta truncate">{describe} ({commit})</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="ml-0.5 cursor-pointer text-muted-foreground/60 hover:text-foreground transition-colors"
        title="접기 (작은 버튼으로)"
        aria-label="collapse"
      >
        ×
      </button>
    </div>
  );
};

export default EnvVersionBadge;
